import { useCallback, useRef, useState } from 'react';

import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Loader2,
  Play,
  Square,
  Trash2,
  Upload,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useCreateBatchRun,
  useDeleteBatchRun,
  useStartBatchRun,
  useStopBatchRun,
  useUploadBatchRun,
  useWorkflowBatchRunRows,
  useWorkflowBatchRuns,
} from '@/hooks/useWorkflowBatchRuns';
import { useWorkflow } from '@/hooks/useWorkflows';
import { extractErrorMessage } from '@/lib/utils';
import { formatDate } from '@/utils/format';

import type { WorkflowBatchRun } from '@/lib/api/workflow-batch.api';

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  PENDING: 'outline',
  RUNNING: 'default',
  COMPLETED: 'secondary',
  FAILED: 'destructive',
  STOPPED: 'outline',
};

const ProgressBar = ({
  completed,
  failed,
  total,
}: {
  completed: number;
  failed: number;
  total: number;
}) => {
  const pctDone = total > 0 ? Math.round(((completed + failed) / total) * 100) : 0;
  const pctOk = total > 0 ? Math.round((completed / total) * 100) : 0;
  const pctFail = total > 0 ? Math.round((failed / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="bg-muted h-2 w-24 overflow-hidden rounded-full">
        <div className="flex h-full">
          <div className="bg-primary h-full transition-all" style={{ width: `${pctOk}%` }} />
          <div className="bg-destructive h-full transition-all" style={{ width: `${pctFail}%` }} />
        </div>
      </div>
      <span className="text-muted-foreground text-xs">{pctDone}%</span>
    </div>
  );
};

const RowDetail = ({ runId }: { runId: string }) => {
  const [page, setPage] = useState(1);
  const { data } = useWorkflowBatchRunRows(runId, { page, limit: 20 });
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  return (
    <div className="bg-muted/30 border-t px-4 py-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">#</TableHead>
            <TableHead>Data</TableHead>
            <TableHead className="w-24">Status</TableHead>
            <TableHead>Error</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="text-muted-foreground text-xs">{row.row_index + 1}</TableCell>
              <TableCell className="max-w-xs truncate font-mono text-xs">
                {JSON.stringify(row.row_data).slice(0, 120)}
              </TableCell>
              <TableCell>
                <Badge variant={STATUS_VARIANT[row.status] ?? 'outline'} className="text-[10px]">
                  {row.status}
                </Badge>
              </TableCell>
              <TableCell className="text-destructive max-w-xs truncate text-xs">
                {row.error_message}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground text-center text-sm">
                No rows
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {totalPages > 1 && (
        <div className="mt-2 flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Prev
          </Button>
          <span className="text-muted-foreground text-xs">
            {page} / {totalPages}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export const BatchRunsPage = () => {
  const { id: workflowId } = useParams<{ id: string }>();
  const { data: workflow } = useWorkflow(workflowId);
  const { data: runsData, isLoading } = useWorkflowBatchRuns(workflowId!);
  const runs = runsData?.data ?? [];

  usePageTitle(workflow ? `Batch Runs — ${workflow.name}` : 'Batch Runs');

  const { toast } = useToast();
  const createMutation = useCreateBatchRun();
  const uploadMutation = useUploadBatchRun();
  const startMutation = useStartBatchRun();
  const stopMutation = useStopBatchRun();
  const deleteMutation = useDeleteBatchRun();

  const [name, setName] = useState('');
  const [delayMs, setDelayMs] = useState(1000);
  const [concurrency, setConcurrency] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [jsonData, setJsonData] = useState<Record<string, unknown>[] | null>(null);
  const [preview, setPreview] = useState<Record<string, unknown>[]>([]);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    (f: File | null) => {
      setFile(f);
      setJsonData(null);
      setPreview([]);
      if (!f) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          let parsed: Record<string, unknown>[];

          if (f.name.endsWith('.json')) {
            parsed = JSON.parse(text);
            if (!Array.isArray(parsed)) {
              toast({ variant: 'destructive', title: 'JSON must be an array of objects' });
              return;
            }
          } else {
            const lines = text.split('\n').filter((l) => l.trim());
            if (lines.length < 2) {
              toast({
                variant: 'destructive',
                title: 'CSV must have a header row and at least one data row',
              });
              return;
            }
            const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
            parsed = lines.slice(1).map((line) => {
              const values = line.split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
              const row: Record<string, unknown> = {};
              headers.forEach((h, i) => {
                row[h] = values[i] ?? '';
              });
              return row;
            });
          }

          setJsonData(parsed);
          setPreview(parsed.slice(0, 5));
        } catch {
          toast({ variant: 'destructive', title: 'Failed to parse file' });
        }
      };
      reader.readAsText(f);
    },
    [toast],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) handleFileChange(droppedFile);
    },
    [handleFileChange],
  );

  const handleCreateAndStart = async () => {
    if (!workflowId) return;
    try {
      let batchRun: WorkflowBatchRun;

      if (jsonData) {
        batchRun = await createMutation.mutateAsync({
          workflow_id: workflowId,
          name: name || undefined,
          delay_ms: delayMs,
          concurrency,
          data: jsonData,
        });
      } else if (file) {
        batchRun = await uploadMutation.mutateAsync({
          file,
          workflowId,
          config: { name: name || undefined, delay_ms: delayMs, concurrency },
        });
      } else {
        toast({ variant: 'destructive', title: 'Upload a file first' });
        return;
      }

      await startMutation.mutateAsync(batchRun.id);
      toast({ variant: 'success', title: 'Batch run started' });
      setFile(null);
      setJsonData(null);
      setPreview([]);
      setName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to start batch run',
        description: extractErrorMessage(err),
      });
    }
  };

  const handleStop = async (id: string) => {
    try {
      await stopMutation.mutateAsync(id);
      toast({ variant: 'success', title: 'Batch run stopped' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to stop',
        description: extractErrorMessage(err),
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast({ variant: 'success', title: 'Batch run deleted' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete',
        description: extractErrorMessage(err),
      });
    }
  };

  const previewHeaders = preview.length > 0 ? Object.keys(preview[0]) : [];

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <Link to="/workflows">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Workflow</p>
          <h1 className="text-2xl font-bold">{workflow?.name ?? 'Loading...'} — Batch Runs</h1>
        </div>
      </header>

      {/* Create section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">New Batch Run</CardTitle>
          <CardDescription>
            Upload CSV or JSON data to process through this workflow.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Name (optional)</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Batch run label"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Delay (ms)</label>
              <Input
                type="number"
                min={0}
                value={delayMs}
                onChange={(e) => setDelayMs(Number(e.target.value))}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Concurrency</label>
              <Input
                type="number"
                min={1}
                max={50}
                value={concurrency}
                onChange={(e) => setConcurrency(Number(e.target.value))}
              />
            </div>
          </div>

          <div
            className="hover:border-foreground/40 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="text-muted-foreground h-8 w-8" />
            <p className="text-muted-foreground mt-2 text-sm">
              {file ? file.name : 'Drop CSV or JSON file here, or click to browse'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.json"
              className="hidden"
              onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
            />
          </div>

          {preview.length > 0 && (
            <div className="space-y-2">
              <p className="text-muted-foreground text-xs">
                Preview ({jsonData?.length ?? 0} rows total, showing first {preview.length})
              </p>
              <div className="overflow-x-auto rounded border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {previewHeaders.map((h) => (
                        <TableHead key={h} className="text-xs">
                          {h}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.map((row, i) => (
                      <TableRow key={i}>
                        {previewHeaders.map((h) => (
                          <TableCell key={h} className="max-w-xs truncate text-xs">
                            {typeof row[h] === 'object'
                              ? JSON.stringify(row[h])
                              : String(row[h] ?? '')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={handleCreateAndStart}
              disabled={
                (!file && !jsonData) ||
                createMutation.isPending ||
                uploadMutation.isPending ||
                startMutation.isPending
              }
            >
              {(createMutation.isPending ||
                uploadMutation.isPending ||
                startMutation.isPending) && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              <Play className="h-4 w-4" />
              Start Batch Run
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Runs list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Batch Runs</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          ) : runs.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">No batch runs yet.</p>
          ) : (
            <div className="space-y-1">
              {runs.map((run) => (
                <div key={run.id} className="rounded-lg border">
                  <div
                    className="hover:bg-muted/50 flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors"
                    onClick={() => setExpandedRun(expandedRun === run.id ? null : run.id)}
                  >
                    {expandedRun === run.id ? (
                      <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
                    ) : (
                      <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
                    )}
                    <div className="flex flex-1 items-center gap-3">
                      <Badge variant={STATUS_VARIANT[run.status] ?? 'outline'}>{run.status}</Badge>
                      <span className="text-sm font-medium">{run.name || 'Unnamed'}</span>
                      <ProgressBar
                        completed={run.completed_rows}
                        failed={run.failed_rows}
                        total={run.total_rows}
                      />
                      <span className="text-muted-foreground text-xs">
                        {run.completed_rows}/{run.total_rows} done
                        {run.failed_rows > 0 && (
                          <span className="text-destructive ml-1">({run.failed_rows} failed)</span>
                        )}
                      </span>
                    </div>
                    <span className="text-muted-foreground text-xs">
                      {formatDate(run.created_at)}
                    </span>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      {run.status === 'RUNNING' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStop(run.id)}
                          disabled={stopMutation.isPending}
                        >
                          <Square className="h-3 w-3" />
                        </Button>
                      )}
                      {run.status !== 'RUNNING' && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(run.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {expandedRun === run.id && <RowDetail runId={run.id} />}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
